import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import SEO from './SEO';

const API_URL = process.env.REACT_APP_API_URL || '/api';

function Competition({ onBack, onViewAuthor, onViewStory }) {
  const [competition, setCompetition] = useState(null);
  const [entries, setEntries] = useState([]);
  const [winners, setWinners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [year, setYear] = useState(new Date().getFullYear());
  const [view, setView] = useState('leaderboard'); // 'leaderboard' | 'winner'
  const headerRef = useRef(null);

  useEffect(() => {
    fetchCompetitionData();
  }, [year]);

  // Scroll to the competition header when the page loads
  useEffect(() => {
    const scrollTimer = setTimeout(() => {
      if (headerRef.current) {
        headerRef.current.scrollIntoView({
          behavior: 'smooth',
          block: 'start'
        });
      }
    }, 100);
    return () => clearTimeout(scrollTimer);
  }, []);

  const fetchCompetitionData = async () => {
    setLoading(true);
    setError(null);
    try {
      // Get competition by year
      const compRes = await axios.get(`${API_URL}/competitions/${year}`);
      if (compRes.data && compRes.data.success) {
        const comp = compRes.data.data;
        setCompetition(comp);

        if (comp && comp.id) {
          // Get entries (leaderboard)
          const entriesRes = await axios.get(`${API_URL}/competitions/${comp.id}/entries?limit=100`);
          if (entriesRes.data && entriesRes.data.success) {
            setEntries(entriesRes.data.data || []);
          }

          // Get winners
          const winnersRes = await axios.get(`${API_URL}/competitions/${comp.id}/winners`);
          if (winnersRes.data && winnersRes.data.success) {
            setWinners(winnersRes.data.data || []);
          }
        } else {
          setEntries([]);
          setWinners([]);
        }
      } else {
        setCompetition(null);
        setEntries([]);
        setWinners([]);
      }
    } catch (err) {
      // 404 = no competition for this year (expected, not an error)
      if (err.response && err.response.status === 404) {
        console.log(`No competition found for year ${year}`);
        setCompetition(null);
        setEntries([]);
        setWinners([]);
      } else {
        console.error('Error fetching competition data:', err);
        setError('Failed to load competition data. Please try again later.');
      }
    } finally {
      setLoading(false);
    }
  };

  // Build Event schema for the competition
  const eventSchema = competition ? {
    '@context': 'https://schema.org',
    '@type': 'Event',
    name: competition.title || `StoryMeal ${year} Writing Competition`,
    description: competition.description || `Annual writing competition on StoryMeal. ${year} edition.`,
    startDate: competition.start_date,
    endDate: competition.end_date,
    eventStatus: 'https://schema.org/EventScheduled',
    eventAttendanceMode: 'https://schema.org/OnlineEventAttendanceMode',
    location: {
      '@type': 'VirtualLocation',
      url: 'https://storymeal.com/competition'
    },
    organizer: {
      '@type': 'Organization',
      name: 'StoryMeal',
      url: 'https://storymeal.com'
    }
  } : null;

  // BreadcrumbList schema
  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'Home',
        item: 'https://storymeal.com/'
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: 'Competition',
        item: 'https://storymeal.com/competition'
      }
    ]
  };

  if (loading) {
    return (
      <div className="competition-page">
        <SEO
          title={`${year} Writing Competition`}
          description={`StoryMeal ${year} writing competition leaderboard and winners.`}
          path="/competition"
          keywords="writing competition, story contest, yearly competition"
        />
        <div className="competition-header">
          <button className="competition-back-btn" onClick={onBack}>← Back</button>
          <h1>🏆 Competition</h1>
        </div>
        <div className="competition-loading">Loading competition data...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="competition-page">
        <SEO
          title={`${year} Writing Competition`}
          description={`StoryMeal ${year} writing competition leaderboard and winners.`}
          path="/competition"
          keywords="writing competition, story contest, yearly competition"
        />
        <div className="competition-header">
          <button className="competition-back-btn" onClick={onBack}>← Back</button>
          <h1>🏆 Competition</h1>
        </div>
        <div className="competition-error">{error}</div>
      </div>
    );
  }

  const hasWinner = winners && winners.length > 0;
  const topWinner = hasWinner ? winners[0] : null;

  return (
    <div className="competition-page">
      {/* SEO Meta Tags for competition */}
      <SEO
        title={`${year} Writing Competition`}
        description={`Join the StoryMeal ${year} writing competition. View the leaderboard, see top stories, and compete with writers worldwide. Winner announced January 31st.`}
        path="/competition"
        keywords={`${year} writing competition, story contest, writing contest, yearly competition, leaderboard`}
        schema={eventSchema}
      />
      <SEO schema={breadcrumbSchema} />

      <div className="competition-header" ref={headerRef}>
        <button className="competition-back-btn" onClick={onBack}>← Back</button>
        <h1>🏆 StoryMeal Competition</h1>
      </div>

      {/* Year Navigation */}
      <nav className="competition-year-nav" aria-label="Year selection">
        <span className="competition-year-label">Year:</span>
        <div className="competition-year-buttons">
          {[2024, 2025, 2026].map(y => (
            <button
              key={y}
              className={`competition-year-btn ${year === y ? 'active' : ''}`}
              onClick={() => setYear(y)}
              aria-pressed={year === y}
            >
              {y}
            </button>
          ))}
        </div>
      </nav>

      {/* Competition Info */}
      {competition && (
        <section className="competition-info-card" aria-label="Competition information">
          <h2>{competition.title}</h2>
          {competition.description && <p>{competition.description}</p>}
          <div className="competition-info-stats">
            <span>📝 {competition.total_entries || 0} entries</span>
            <span>🗳️ {competition.total_votes || 0} votes</span>
            <span>📅 {competition.start_date?.substring(0, 10)} → {competition.end_date?.substring(0, 10)}</span>
          </div>
        </section>
      )}

      {/* Tab Navigation */}
      <div className="competition-tabs" role="tablist">
        <button
          role="tab"
          aria-selected={view === 'leaderboard'}
          className={`competition-tab ${view === 'leaderboard' ? 'active' : ''}`}
          onClick={() => setView('leaderboard')}
        >
          📊 Leaderboard
        </button>
        <button
          role="tab"
          aria-selected={view === 'winner'}
          className={`competition-tab ${view === 'winner' ? 'active' : ''}`}
          onClick={() => setView('winner')}
        >
          🏆 Yearly Winner
        </button>
      </div>

      {/* ─── LEADERBOARD VIEW ────────────────────────────── */}
      {view === 'leaderboard' && (
        <section className="competition-leaderboard" role="tabpanel" aria-label="Leaderboard">
          <p className="competition-description">
            Rankings based on <strong>points</strong>: each view = 1 point, each like = 5 points.
            The yearly winner is declared on <strong>January 31st</strong> each year.
          </p>

          {!competition ? (
            <div className="competition-empty">
              <div className="competition-empty-icon">📅</div>
              <h3>No competition for {year}</h3>
              <p>Check back later or select a different year.</p>
            </div>
          ) : entries.length === 0 ? (
            <div className="competition-empty">
              <div className="competition-empty-icon">📊</div>
              <h3>No entries yet for {year}</h3>
              <p>Be the first to submit a story to the competition!</p>
            </div>
          ) : (
            <div className="competition-leaderboard-table">
              <div className="competition-table-header">
                <span className="competition-rank-col">#</span>
                <span className="competition-author-col">Story / Author</span>
                <span className="competition-stats-col">Genre</span>
                <span className="competition-stats-col">👁️ Views</span>
                <span className="competition-stats-col">❤️ Likes</span>
                <span className="competition-score-col">🏆 Points</span>
              </div>
              {entries.map((entry, index) => (
                <div
                  key={entry.id || index}
                  className={`competition-table-row ${index < 3 ? 'top-three' : ''} ${entry.is_winner ? 'is-winner' : ''}`}
                  onClick={() => entry.story_id && onViewStory && onViewStory(entry.story_id)}
                  style={{ cursor: entry.story_id ? 'pointer' : 'default' }}
                  role="row"
                >
                  <span className="competition-rank-col">
                    {index === 0 ? '🥇' : index === 1 ? '🥈' : index === 2 ? '🥉' : `#${index + 1}`}
                  </span>
                  <span className="competition-author-col">
                    <strong>{entry.title || 'Untitled'}</strong>
                    <small className="competition-entry-author">by {entry.author_name || entry.display_name || entry.pet_name || 'Anonymous'}</small>
                  </span>
                  <span className="competition-stats-col">{entry.genre || '-'}</span>
                  <span className="competition-stats-col">{entry.current_view_count || entry.view_count || 0}</span>
                  <span className="competition-stats-col">{entry.current_like_count || entry.like_count || 0}</span>
                  <span className="competition-score-col">
                    {entry.is_winner ? (
                      <span className="winner-badge">🏆 Winner #{entry.winner_rank}</span>
                    ) : (
                      <span className="score-badge">{entry.points || 0} pts</span>
                    )}
                  </span>
                </div>
              ))}
            </div>
          )}
        </section>
      )}

      {/* ─── YEARLY WINNER VIEW ─────────────────────────────── */}
      {view === 'winner' && (
        <section className="competition-winner-section" role="tabpanel" aria-label="Yearly winner">
          {!competition ? (
            <div className="competition-no-winner">
              <div className="competition-no-winner-icon">📅</div>
              <h3>No competition for {year}</h3>
              <p className="competition-winner-date">
                The yearly winner is announced on <strong>January 31st</strong> each year.
              </p>
            </div>
          ) : !hasWinner ? (
            <div className="competition-no-winner">
              <div className="competition-no-winner-icon">🏆</div>
              <h3>Winner not yet declared for {year}</h3>
              <p className="competition-winner-date">
                The yearly winner is announced on <strong>January 31st</strong> each year.
              </p>
              <p>Check back on January 31st to see who won!</p>
            </div>
          ) : (
            <div>
              <article className="competition-winner-card">
                <div className="competition-winner-crown">👑</div>
                <div className="competition-winner-avatar">
                  {topWinner.author_name?.charAt(0)?.toUpperCase() || topWinner.display_name?.charAt(0)?.toUpperCase() || '?'}
                </div>
                <div className="competition-winner-name">
                  <h2>{topWinner.author_name || topWinner.display_name || topWinner.pet_name}</h2>
                  <p className="competition-winner-story">"{topWinner.title}"</p>
                </div>
                <div className="competition-winner-stats">
                  <div className="winner-stat">
                    <span className="winner-stat-label">Year</span>
                    <span className="winner-stat-value">{year}</span>
                  </div>
                  <div className="winner-stat">
                    <span className="winner-stat-label">Declared</span>
                    <span className="winner-stat-value">January 31st</span>
                  </div>
                  <div className="winner-stat">
                    <span className="winner-stat-label">Points</span>
                    <span className="winner-stat-value">{topWinner.points || 0}</span>
                  </div>
                </div>
                {topWinner.winner_prize && (
                  <div className="competition-winner-message">
                    {topWinner.winner_prize}
                  </div>
                )}
                {topWinner.winner_award_description && (
                  <div className="competition-winner-award">
                    {topWinner.winner_award_description}
                  </div>
                )}
              </article>

              {/* Show all winners if there are multiple */}
              {winners.length > 1 && (
                <div className="competition-all-winners">
                  <h3>🏆 All Winners for {year}</h3>
                  {winners.map((w) => (
                    <div key={w.id} className="competition-winner-row">
                      <span className="competition-winner-rank">#{w.winner_rank}</span>
                      <span className="competition-winner-info">
                        <strong>{w.author_name || w.display_name || w.pet_name}</strong>
                        <small>"{w.title}"</small>
                      </span>
                      <span className="competition-winner-votes">{w.points || 0} pts</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </section>
      )}
    </div>
  );
}

export default Competition;